import { formatCurrency, formatNumber } from '@angular/common';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CurrencyFormatOptions } from '../interfaces/currency-format-options';

interface CurrencyServiceData {
    options: CurrencyFormatOptions;
}

/// A basket in the currency the visitor is shown: every figure already converted, and each sum made of the figures
/// shown - so the lines add up to the subtotal and the subtotal and extras to the total, to the last cent.
export interface LocalBasket {
    /// Each line: the unit price converted once, times the quantity.
    lines: number[];
    subtotal: number;
    /// Each extra (delivery, a discount) converted on its own, in the order given.
    extras: number[];
    /// For an extra given a `before` (delivery before the free-delivery offer): that figure converted, else null.
    before: (number | null)[];
    /// And the saving shown for it: extra - before, in whole minor units, so the two lines add up to the extra exactly.
    savings: (number | null)[];
    total: number;
    /// Shown in something other than the site's own currency.
    isLocal: boolean;
}

/// The site's own currency (SiteMoney on the server, sent with every Locale/me answer as SiteCurrency*): every stored
/// amount is in it - prices, delivery fees, orders, balances - and the owner's and sellers' screens show and take it.
export interface SiteCurrency {
    code: string;
    symbol: string;
    name: string;
    decimals: number;
    /// The one country the site sells in (SiteCountryCode), or '': every country.
    country: string;
}

/// What an order (or an order line) says about the currency it was charged in - Order.ChargedCurrency and friends on
/// the server, worked out from its payment method, Currency and CurrencyRate.
export interface ChargedHolder {
    ChargedCurrency?: string;
    ChargedSymbol?: string;
    ChargedDecimals?: number;
    CurrencyRate?: number;
}

/// A past order's figures in the currency it was charged in.
export interface OrderCharge {
    lines: number[];
    subtotal: number;
    shipping: number;
    /// Delivery before the free-delivery offer, and the offer's saving (shippingBefore - shipping) - 0 when there was none.
    shippingBefore: number;
    shippingOffer: number;
    discount: number;
    total: number;
}

/// How prices are shown (Locale/me, ExchangeRates on the server): the currency, its symbol and decimals, and the rate
/// from the site's own currency (`site`), which every price is stored in. One rounding everywhere - half away from zero,
/// to the currency's decimals - the same as the server's CurrencyCatalog.Convert.
@Injectable({
    providedIn: 'root'
})
export class CurrencyService {
    /// The site's currency: set from the first Locale/me answer (app.component), before anything is drawn from it.
    private static siteCurrency: SiteCurrency = { code: '', symbol: '', name: '', decimals: 2, country: '' };

    static get site(): SiteCurrency {
        return CurrencyService.siteCurrency;
    }

    get site(): SiteCurrency {
        return CurrencyService.siteCurrency;
    }

    /// From a Locale/me answer; an answer without it (an old one kept in storage) leaves what is known.
    static useSite(info: { SiteCurrencyCode?: string, SiteCurrencySymbol?: string, SiteCurrencyName?: string, SiteCurrencyDecimals?: number,
                           SiteCountryCode?: string }): void {
        if (!info || !info.SiteCurrencyCode) {
            return;
        }
        const d = info.SiteCurrencyDecimals;
        CurrencyService.siteCurrency = {
            code: info.SiteCurrencyCode,
            symbol: info.SiteCurrencySymbol || info.SiteCurrencyCode,
            name: info.SiteCurrencyName || info.SiteCurrencyCode,
            decimals: d === 0 || d === 3 ? d : 2,
            country: info.SiteCountryCode || '',
        };
    }

    /// An amount in the site's currency, written: "৳1,234.50", "$12.00". Plain digits while the currency is not known yet.
    static writeSite(amount: number): string {
        const s = CurrencyService.siteCurrency;
        const d = s.decimals;
        const rounded = CurrencyService.round(amount, d);
        if (!s.code) {
            return formatNumber(rounded, 'en-US', `1.${d}-${d}`);
        }
        const symbol = /^[A-Za-z]+$/.test(s.symbol) ? s.symbol + ' ' : s.symbol;
        return formatCurrency(rounded, 'en-US', symbol, s.code, `1.${d}-${d}`);
    }
    private data: CurrencyServiceData = {
        options: {}
    };

    private changesSubject$: Subject<CurrencyFormatOptions> = new Subject();

    changes$: Observable<CurrencyFormatOptions> = this.changesSubject$.asObservable();

    get options(): CurrencyFormatOptions {
        return this.data.options;
    }
    set options(value: CurrencyFormatOptions) {
        this.data.options = value;
        this.changesSubject$.next(value);
    }

    constructor() {}

    /// Decimals the shown currency is rounded and written with (0 for yen, won, dong...).
    get decimals(): number {
        const d = this.options.decimals;
        return d === 0 || d === 3 ? d : 2;
    }

    get isLocal(): boolean {
        return !!this.options.code && this.options.code !== this.site.code && (this.options.multiplier ?? 1) > 0;
    }

    /// The signed-in owner picked a currency in the top bar and prices are shown in it (OwnerCurrency on the server): the
    /// stored amounts on their own screens are shown in it as well, at the same rate (the owner, 2026-10-03). Never for
    /// anybody else, and not for "Automatic" - their screens then stay in the site's currency.
    get ownerConverts(): boolean {
        return !!this.options.ownerPick && this.isLocal;
    }

    /// An amount already in the shown currency, written as the currency pipes write it (CurrencyPipe is formatCurrency):
    /// its symbol, its decimals, grouped the en-US way. Null for something that is not a number, as the pipes give.
    written(amount: number): string | null {
        if (amount === null || amount === undefined || amount !== amount) {
            return null;
        }
        if (!this.isLocal) {
            return CurrencyService.writeSite(amount);
        }
        const d = this.decimals;
        const symbol = typeof this.options.display === 'string' && this.options.display ? this.options.display : this.options.code;
        return formatCurrency(amount, 'en-US', symbol, this.options.code, `1.${d}-${d}`);
    }

    /// A stored amount as a screen of the owner's or a seller's writes it (`currencyFormat:'stored'`): in the currency the
    /// owner picked (ownerConverts), converted as every price is; otherwise in the site's currency, as it is.
    stored(amount: number): string | null {
        if (amount === null || amount === undefined || amount !== amount) {
            return null;
        }
        return this.ownerConverts ? this.written(this.convert(amount)) : CurrencyService.writeSite(amount);
    }

    /// An amount quoted in a currency the data names - a supplier's price or delivery fee (`quotedMoney:row.Currency`):
    /// the site's currency, or no currency named, is a stored amount; any other is written as it is, never converted.
    quoted(amount: number, code: string): string | null {
        const currency = (code || this.site.code).trim().toUpperCase();
        if (currency === this.site.code) {
            return this.stored(amount);
        }
        return amount === null || amount === undefined || amount !== amount ? null : formatNumber(amount, 'en-US', '1.2-2') + ' ' + currency;
    }

    /// Half away from zero to `decimals`, without binary noise: 1.005 * 100 is 100.49999999999999 in floating point,
    /// which would round down; it is read as 100.5 first. 15 significant digits is what a double holds exactly.
    static round(value: number, decimals: number): number {
        const factor = Math.pow(10, decimals);
        const scaled = Number((Math.abs(value || 0) * factor).toPrecision(15));
        return (value < 0 ? -1 : 1) * Math.round(scaled) / factor;
    }

    /// A stored amount as it is shown: at the rate, rounded to the shown currency's decimals. In the site's own currency it
    /// stays as it is, to that currency's decimals.
    convert(amount: number): number {
        if (!this.isLocal) {
            return CurrencyService.round(amount, this.site.decimals);
        }
        return CurrencyService.round((amount || 0) * (this.options.multiplier ?? 1), this.decimals);
    }

    /// A line as shown: the unit price converted once, times the quantity - never the line's dollars converted, which
    /// could differ by a cent from the unit price the page shows beside it.
    lineTotal(unitUsd: number, quantity: number): number {
        const d = this.isLocal ? this.decimals : this.site.decimals;
        const minor = Math.round(this.convert(unitUsd) * Math.pow(10, d));
        return (minor * (quantity || 0)) / Math.pow(10, d);
    }

    /// The basket as shown, summed in whole minor units (cents) so no floating-point dust reaches the total.
    basket(lines: { price: number, quantity: number }[], extras: { price: number, before?: number }[]): LocalBasket {
        const d = this.isLocal ? this.decimals : this.site.decimals;
        const factor = Math.pow(10, d);
        const lineMinor = (lines || []).map(l => Math.round(this.convert(l.price) * factor) * (l.quantity || 0));
        const extraMinor = (extras || []).map(e => Math.round(this.convert(e.price) * factor));
        const beforeMinor = (extras || []).map(e => e.before != null && e.before > e.price ? Math.round(this.convert(e.before) * factor) : null);
        const subtotalMinor = lineMinor.reduce((a, b) => a + b, 0);
        const totalMinor = subtotalMinor + extraMinor.reduce((a, b) => a + b, 0);
        return {
            lines: lineMinor.map(m => m / factor),
            subtotal: subtotalMinor / factor,
            extras: extraMinor.map(m => m / factor),
            before: beforeMinor.map(m => m == null ? null : m / factor),
            savings: beforeMinor.map((m, i) => m == null ? null : (extraMinor[i] - m) / factor),
            total: totalMinor / factor,
            isLocal: this.isLocal,
        };
    }

    /// The rate and decimals a past order (or one of its lines) was charged at: a card order in its own currency at its
    /// own rate, anything else in the site's (Order.ChargedCurrency/ChargedDecimals on the server).
    static chargedAt(h: ChargedHolder): { rate: number, decimals: number } {
        const local = !!h && !!h.ChargedCurrency && h.ChargedCurrency !== CurrencyService.site.code && (h.CurrencyRate || 0) > 0;
        const named = h && (h.ChargedDecimals === 0 || h.ChargedDecimals === 3) ? h.ChargedDecimals : 2;
        return { rate: local ? h.CurrencyRate : 1, decimals: local ? named : CurrencyService.site.decimals };
    }

    /// A US-dollar amount in the order's charged currency, in its minor units: the same rounding the basket and the
    /// payment used.
    static chargedMinor(usd: number, h: ChargedHolder): number {
        const { rate, decimals } = CurrencyService.chargedAt(h);
        return Math.round(CurrencyService.round((usd || 0) * rate, decimals) * Math.pow(10, decimals));
    }

    /// A line as it was charged: the unit converted once, times the quantity.
    static chargedLine(unitUsd: number, quantity: number, h: ChargedHolder): number {
        return CurrencyService.chargedMinor(unitUsd, h) * (quantity || 0) / Math.pow(10, CurrencyService.chargedAt(h).decimals);
    }

    /// What a past order was charged, figure by figure - the sums made of the converted figures, as the payment was
    /// (server: ChargedAmounts), so the page shows what the card statement shows.
    static orderCharge(order: ChargedHolder & { ShippingCharge?: number, ShippingOffer?: number, Discount?: number },
                       items: { unit: number, quantity: number }[]): OrderCharge {
        const factor = Math.pow(10, CurrencyService.chargedAt(order).decimals);
        const lines = (items || []).map(i => CurrencyService.chargedMinor(i.unit, order) * (i.quantity || 0));
        const subtotal = lines.reduce((a, b) => a + b, 0);
        const shipping = CurrencyService.chargedMinor(Math.max(0, order?.ShippingCharge || 0), order);
        // Server: ChargedAmounts.ShippingBefore - the full delivery converted on its own, the saving the difference.
        const offerUsd = Math.max(0, order?.ShippingOffer || 0);
        const before = offerUsd > 0 ? Math.max(shipping, CurrencyService.chargedMinor(Math.max(0, order?.ShippingCharge || 0) + offerUsd, order)) : shipping;
        const discount = CurrencyService.chargedMinor(Math.max(0, order?.Discount || 0), order);
        return {
            lines: lines.map(m => m / factor),
            subtotal: subtotal / factor,
            shipping: shipping / factor,
            shippingBefore: before / factor,
            shippingOffer: (before - shipping) / factor,
            discount: discount / factor,
            total: (subtotal + shipping - discount) / factor,
        };
    }

    /**
     * A price split for the large-whole, small-cents display: converted as every other price is, and the whole part
     * grouped the way the currency pipe groups every other price - 1,137 and 30, never 1137. No fraction for a
     * currency with no decimals (yen, won), so the templates write no stray point.
     */
    priceParts(amount: number): { whole: string, fraction: string } {
        const converted = this.convert(amount);
        const d = this.isLocal ? this.decimals : this.site.decimals;
        const [digits, fraction] = converted.toFixed(d).split('.');
        return { whole: digits.replace(/\B(?=(\d{3})+(?!\d))/g, ','), fraction: fraction || '' };
    }
}
